import { Fragment, useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import HeroBillboard from "../components/HeroBillboard";
import PosterRow from "../components/PosterRow";
import IndieTeaser from "../components/IndieTeaser";
import PodcastSpotlight from "../components/PodcastSpotlight";
import GenreChipRow from "../components/GenreChipRow";
import GuideHome from "./GuideHome";
import SocialHome from "./SocialHome";
import ClickWheelHome from "./ClickWheelHome";
import Disclaimer from "../components/Disclaimer";
import api from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";

export default function Home() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [followRow, setFollowRow] = useState([]);
  const [lovedRow, setLovedRow] = useState([]);
  const [myList, setMyList] = useState([]);
  const { layout, isLight } = useTheme();
  const { user } = useAuth();
  const overlayNav = layout.hero === "billboard";
  const guide = layout.home === "guide";
  const social = layout.home === "social";
  const wheel = layout.home === "wheel";

  useEffect(() => {
    if (guide || social || wheel) return;
    api.get("/catalog/home").then(({ data }) => setData(data)).catch(() => setError(true));
    api.get("/catalog/following-feed").then(({ data }) => setFollowRow(data.items || [])).catch(() => {});
    api.get("/catalog/loved-by-friends").then(({ data }) => setLovedRow(data.items || [])).catch(() => {});
  }, [guide, social, wheel]); // eslint-disable-line react-hooks/exhaustive-deps

  // My List row: appears the moment the first favorite is saved, updates live, hides at zero.
  const wlKey = (user?.watchlist || []).join(",");
  useEffect(() => {
    if (guide) return;
    let alive = true;
    const ids = user?.watchlist || [];
    if (!ids.length) { setMyList([]); return; }
    let tries = 0;
    const run = () => api.get("/watchlist").then(({ data }) => {
      if (!alive) return;
      const items = data.items || [];
      setMyList(items);
      if (items.length !== ids.length && tries < 3) { tries++; setTimeout(run, 400); }
    }).catch(() => {});
    run();
    return () => { alive = false; };
  }, [wlKey, guide]);

  // persist + restore Home scroll position across sessions
  useEffect(() => {
    const onScroll = () => sessionStorage.setItem("fh_home_scroll", String(window.scrollY));
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!data) return;
    const y = parseInt(sessionStorage.getItem("fh_home_scroll") || "0", 10);
    if (y > 0) {
      const t = setTimeout(() => window.scrollTo(0, y), 60);
      return () => clearTimeout(t);
    }
  }, [data]);

  if (guide) return <GuideHome />;
  if (social) return <SocialHome />;
  if (wheel) return <ClickWheelHome />;

  return (
    <div className={`${isLight ? "" : "grain"} min-h-screen bg-[var(--bg-base)]`}>
      {overlayNav ? <div className="absolute top-0 left-0 right-0 z-50"><Navbar /></div> : <Navbar />}

      {!data && !error && (
        <div className="flex h-screen items-center justify-center">
          <div className="font-head text-3xl tracking-widest text-[var(--accent)] animate-pulse">Loading…</div>
        </div>
      )}

      {error && (
        <div className="flex h-screen items-center justify-center text-[var(--text-secondary)]" data-testid="home-error">
          Could not load catalog.
        </div>
      )}

      {data && (
        <>
          <HeroBillboard heroes={data.heroes && data.heroes.length ? data.heroes : [data.hero]} />
          <div className={`relative z-20 pb-20 ${overlayNav ? "-mt-8" : "mt-4"}`} data-testid="home-rows">
            <GenreChipRow className="mb-4 px-4 lg:px-14" />
            {myList.length > 0 && (
              <PosterRow title="My List" items={myList} testid="row-my_list" />
            )}
            {lovedRow.length > 0 && (
              <PosterRow title="Loved by Friends" items={lovedRow} testid="row-loved_by_friends" />
            )}
            {followRow.length > 0 && (
              <PosterRow title="New From People You Follow" items={followRow} testid="row-following" />
            )}
            {data.rows.map((row) => (
              <Fragment key={row.key}>
                <PosterRow title={row.title} items={row.items} testid={`row-${row.key}`} seeAll={row.key === "indie_spotlight" ? "/indie" : undefined} />
                {row.key === "indie_spotlight" && <PodcastSpotlight />}
              </Fragment>
            ))}
            {data.indie_teaser && <><IndieTeaser /><PodcastSpotlight /></>}
          </div>
          <Disclaimer />
        </>
      )}
    </div>
  );
}
